import React, { useEffect, useMemo } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";

import GuestLayout from "@/components/guest/GuestLayout.jsx";
import { useSession } from "@/hooks/useSession.js";

import {
  Backpack as Passport,
  Phone,
  DollarSign,
  Wifi,
  Loader2,
  HelpCircle,
} from "lucide-react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const GlassInfoCard = ({ children, className = "" }) => {
  return (
    <div
      className={`
        rounded-3xl border border-white/40
        bg-white/75 backdrop-blur-md
        shadow-none
        ${className}
      `}
    >
      {children}
    </div>
  );
};

const TravelHelpPage = () => {
  const navigate = useNavigate();

  const {
    session,
    hotel,
    room,
    loading: sessionLoading,
  } = useSession();

  useEffect(() => {
    if (!sessionLoading && !session) {
      navigate("/guest/expired", { replace: true });
    }
  }, [sessionLoading, session, navigate]);

  const hotelPhone = hotel?.phone || hotel?.contactNumber || "+977-1-4700123";

  const helpSections = useMemo(
    () => [
      {
        icon: Passport,
        title: "Visa Information",
        content: (
          <div className="space-y-3">
            <p>
              Most visitors can obtain a visa on arrival at Tribhuvan
              International Airport.
            </p>

            <div>
              <p className="font-semibold text-slate-800">Visa fees:</p>

              <ul className="mt-2 list-inside list-disc space-y-1">
                <li>15 days: $30</li>
                <li>30 days: $50</li>
                <li>90 days: $125</li>
              </ul>
            </div>

            <p>
              Required documents: Valid passport with 6 months validity,
              passport photo, and visa application form.
            </p>
          </div>
        ),
      },
      {
        icon: Phone,
        title: "Emergency Contacts",
        content: (
          <div className="space-y-3">
            <p className="font-semibold text-slate-800">Important numbers:</p>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div className="rounded-2xl bg-white/70 px-4 py-3">
                <p className="text-xs text-slate-500">Police</p>
                <p className="font-bold text-slate-800">100</p>
              </div>

              <div className="rounded-2xl bg-white/70 px-4 py-3">
                <p className="text-xs text-slate-500">Ambulance</p>
                <p className="font-bold text-slate-800">102</p>
              </div>

              <div className="rounded-2xl bg-white/70 px-4 py-3">
                <p className="text-xs text-slate-500">Fire</p>
                <p className="font-bold text-slate-800">101</p>
              </div>

              <div className="rounded-2xl bg-white/70 px-4 py-3">
                <p className="text-xs text-slate-500">Tourist Police</p>
                <p className="font-bold text-slate-800">1144</p>
              </div>

              <div className="rounded-2xl bg-white/70 px-4 py-3 sm:col-span-2">
                <p className="text-xs text-slate-500">Hotel Front Desk</p>
                <p className="font-bold text-slate-800">{hotelPhone}</p>
              </div>
            </div>
          </div>
        ),
      },
      {
        icon: DollarSign,
        title: "Currency Information",
        content: (
          <div className="space-y-3">
            <p>The official currency is Nepalese Rupee (NPR).</p>

            <div>
              <p className="font-semibold text-slate-800">
                Exchange rates approximate:
              </p>

              <ul className="mt-2 list-inside list-disc space-y-1">
                <li>1 USD ≈ 132 NPR</li>
                <li>1 EUR ≈ 145 NPR</li>
                <li>1 GBP ≈ 168 NPR</li>
              </ul>
            </div>

            <p>
              ATMs are widely available in major cities. Credit cards are
              accepted at most hotels and restaurants.
            </p>
          </div>
        ),
      },
      {
        icon: Wifi,
        title: "SIM & Internet",
        content: (
          <div className="space-y-3">
            <p>Major telecom providers: Ncell and Nepal Telecom (NTC).</p>

            <div>
              <p className="font-semibold text-slate-800">
                Tourist SIM cards are available at:
              </p>

              <ul className="mt-2 list-inside list-disc space-y-1">
                <li>Airport arrival hall</li>
                <li>Authorized retailers in Thamel</li>
                <li>Hotel front desk assistance</li>
              </ul>
            </div>

            <p>
              Typical cost: $5-10 for a tourist package with data and calls.
            </p>

            <p>Free WiFi is available in hotel common areas and guest rooms.</p>
          </div>
        ),
      },
    ],
    [hotelPhone]
  );

  if (sessionLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!session) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>{`Travel Help - ${hotel?.name || "The Hotelier"}`}</title>
        <meta
          name="description"
          content="Essential travel information for your stay in Nepal"
        />
      </Helmet>

      <GuestLayout hotel={hotel} room={room} session={session}>
        <style>
          {`
            .guest-travel-help-glass,
            .guest-travel-help-glass * {
              box-shadow: none !important;
            }

            .guest-travel-help-glass [data-state="open"] {
              box-shadow: none !important;
            }
          `}
        </style>

        <div className="guest-travel-help-glass mx-auto max-w-3xl space-y-6">
          <div>
            <h1 className="mb-2 text-3xl font-bold text-white text-balance">
              Travel Help
            </h1>

            <p className="text-sm text-white/75">
              Essential information for your stay in Nepal.
            </p>
          </div>

          <Accordion type="single" collapsible className="w-full space-y-4">
            {helpSections.map((section, index) => {
              const Icon = section.icon;

              return (
                <AccordionItem
                  key={section.title}
                  value={`item-${index}`}
                  className="
                    overflow-hidden rounded-3xl
                    border border-white/40
                    bg-white/75 px-5
                    backdrop-blur-md
                    shadow-none
                  "
                >
                  <AccordionTrigger
                    className="
                      min-h-[92px]
                      py-5 hover:no-underline
                      [&>svg]:text-slate-500
                    "
                  >
                    <div className="flex items-center gap-4 text-left">
                      <div
                        className="
                          flex h-[58px] w-[58px] shrink-0 items-center justify-center
                          rounded-2xl bg-[#eadfdf]/90 text-[#923536]
                        "
                      >
                        <Icon className="h-7 w-7" />
                      </div>

                      <div>
                        <span className="block text-lg font-bold text-slate-800">
                          {section.title}
                        </span>

                        <span className="mt-1 block text-sm font-normal text-slate-600">
                          Tap to view details
                        </span>
                      </div>
                    </div>
                  </AccordionTrigger>

                  <AccordionContent className="pb-6 pt-1">
                    <div className="rounded-2xl bg-white/60 p-5 text-sm leading-relaxed text-slate-600">
                      {section.content}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>

          <GlassInfoCard className="p-6">
            <div className="flex gap-4">
              <div className="flex h-[56px] w-[56px] shrink-0 items-center justify-center rounded-2xl bg-[#eadfdf]/90 text-[#923536]">
                <HelpCircle className="h-7 w-7" />
              </div>

              <div>
                <p className="text-base font-bold text-slate-800">
                  Need more help?
                </p>

                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  Our front desk staff is available 24/7 to assist you with any
                  questions or concerns. Call {hotelPhone} or visit the
                  reception desk.
                </p>
              </div>
            </div>
          </GlassInfoCard>
        </div>
      </GuestLayout>
    </>
  );
};

export default TravelHelpPage;
